import { Transformation } from 'app/model/dataflow/transformation';
import { NamedArray } from 'app/model/base/namedarray';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { Owned, Metamodel } from 'shell-models-lib';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { IDataFlowDataType, DecimalDataFlowType, ArrayDataFlowType, MapDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { ComplexDataFlowType, ComplexField, ExternalDataFlowType } from 'app/model/dataflow/dataflow-complex-datatype';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ColumnPlainObject, ColumnType, ColumnDecimalType, ColumnComplexType, ColumnArrayType, ColumnMapType, ColumnExternalType } from 'worker/app-workers/shared/plainObjects/column.plainObject';
import { DataFlowExpressionUtils } from '../../expression.util';

export class SchemaConvertor {
    public static convertColumnsFromPlainObject(columnObjects: ColumnPlainObject[], transformation: Transformation): NamedArray<DataFlowColumn> {
        const schema = NamedArray.create<DataFlowColumn>(transformation);

        columnObjects.forEach((columnObject: ColumnPlainObject) => {
            const sourceColumn = SchemaConvertor.convertColumnFromPlainObject(columnObject, transformation);
            schema.push(sourceColumn);
        });
        return schema;
    }

    public static convertColumnFromPlainObject(columnObject: ColumnPlainObject, transformation: Transformation): DataFlowColumn {
        if (!columnObject) {
            return undefined;
        }

        const sourceColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
        sourceColumn.name = columnObject.name;
        sourceColumn.dataFlowColumnType = SchemaConvertor.convertDataType(columnObject.columnType);
        sourceColumn.dataFlowColumnType.format = columnObject.columnType.format ? DataFlowExpressionUtils.unescapeSingleQuoteInDslString(columnObject.columnType.format) : undefined;
        return sourceColumn;
    }

    public static convertDataType(columnType: ColumnType): IDataFlowDataType {
        let dataType: IDataFlowDataType;
        if (columnType.columnType === 'decimal') {
            dataType = DataFlowTypesUtils.createType(DecimalDataFlowType.TYPE);
            (dataType as DecimalDataFlowType).precision = (columnType as ColumnDecimalType).precision;
            (dataType as DecimalDataFlowType).scale = (columnType as ColumnDecimalType).scale;
        } else if (columnType.columnType === 'complex') {
            dataType = DataFlowTypesUtils.createType(ComplexDataFlowType.TYPE);
            (columnType as ColumnComplexType).fields.forEach((field) => {
                const nestedField: IDataFlowDataType = SchemaConvertor.convertDataType(field.columnType);
                const dataFlowExpression = new DataFlowExpression();
                dataFlowExpression.expression = '';
                dataFlowExpression.returnType = nestedField;
                (dataType as ComplexDataFlowType).fields.push(new ComplexField(field.name, dataFlowExpression, dataType as ComplexDataFlowType));
            });
        } else if (columnType.columnType === 'array') {
            dataType = DataFlowTypesUtils.createType(ArrayDataFlowType.TYPE);
            (dataType as ArrayDataFlowType).arrayType = SchemaConvertor.convertDataType((columnType as ColumnArrayType).arrayType);
        } else if (columnType.columnType === 'map') {
            const mapTypeColumn = columnType as ColumnMapType;
            dataType = DataFlowTypesUtils.createType(MapDataFlowType.TYPE, mapTypeColumn.keyType.columnType, mapTypeColumn.valueType.columnType);
            (dataType as MapDataFlowType).mapKeyType = SchemaConvertor.convertDataType(mapTypeColumn.keyType);
            (dataType as MapDataFlowType).mapValueType = SchemaConvertor.convertDataType(mapTypeColumn.valueType);
        } else if (columnType.columnType === 'external') {
            const externalTypeColumn = columnType as ColumnExternalType;
            dataType = DataFlowTypesUtils.createType(ExternalDataFlowType.TYPE);
            const dataFlowExpression = new DataFlowExpression();
            dataFlowExpression.expression = externalTypeColumn.expression ? externalTypeColumn.expression : '';
            (dataType as ExternalDataFlowType).expression = dataFlowExpression;
        } else {
            dataType = DataFlowTypesUtils.createType(columnType.columnType);
        }
        return dataType;
    }
}
